import { z } from "zod";

export const RuntimeStatusSchema = z
  .object({
    organizationId: z.string().optional(),
    executionMode: z.enum(["mock", "shadow", "live"]).default("shadow"),
    state: z.enum(["running", "paused", "killed", "degraded"]).default("running"),
    activeWorkflows: z.number().int().nonnegative().default(0),
    pendingActions: z.number().int().nonnegative().default(0),
    openIncidents: z.number().int().nonnegative().default(0),
    updatedAt: z.string().default(() => new Date(0).toISOString())
  })
  .passthrough();
export type RuntimeStatus = z.infer<typeof RuntimeStatusSchema>;

export const BudgetStatusSchema = z
  .object({
    organizationId: z.string(),
    currency: z.string().default("EUR"),
    monthlyLimit: z.number().finite().nonnegative(),
    committed: z.number().finite().nonnegative().default(0),
    spent: z.number().finite().nonnegative().default(0),
    remaining: z.number().finite().nonnegative(),
    periodStart: z.string(),
    periodEnd: z.string()
  })
  .passthrough();
export type BudgetStatus = z.infer<typeof BudgetStatusSchema>;

export const AuditEventSchema = z
  .object({
    id: z.string(),
    sequence: z.number().int().nonnegative(),
    occurredAt: z.string(),
    eventType: z.string(),
    outcome: z.string().optional(),
    actor: z.string().optional(),
    summary: z.string().optional(),
    correlationId: z.string().optional()
  })
  .passthrough();
export type AuditEvent = z.infer<typeof AuditEventSchema>;

export const AuditPageSchema = z
  .object({
    events: z.array(AuditEventSchema),
    nextSequence: z.number().int().nonnegative().optional(),
    nextAfterSequence: z.number().int().nonnegative().optional()
  })
  .transform(({ events, nextSequence, nextAfterSequence }) => ({
    events,
    ...((nextSequence ?? nextAfterSequence) === undefined
      ? {}
      : { nextSequence: nextSequence ?? nextAfterSequence })
  }));
export type AuditPage = z.infer<typeof AuditPageSchema>;

export const OwnerResourceSchema = z.enum([
  "portfolio",
  "opportunities",
  "evidence",
  "products",
  "deployments",
  "metrics",
  "costs",
  "reconciliations",
  "incidents",
  "gates"
]);
export type OwnerResource = z.infer<typeof OwnerResourceSchema>;

export const CursorSchema = z.string().min(1).max(2048).regex(/^[A-Za-z0-9_-]+$/u);
export const PaginationInputSchema = z
  .object({
    limit: z.number().int().min(1).max(100).default(50),
    cursor: CursorSchema.optional()
  })
  .strict();
export type PaginationInput = z.input<typeof PaginationInputSchema>;

export interface OwnerPage<T> {
  readonly items: readonly T[];
  readonly nextCursor?: string;
}

export const OwnerPageEnvelopeSchema = z
  .object({
    resource: OwnerResourceSchema,
    items: z.array(z.unknown()),
    nextCursor: CursorSchema.optional()
  })
  .strict();

export const WorkflowPageEnvelopeSchema = z
  .object({
    workflows: z.array(z.unknown()),
    nextCursor: CursorSchema.optional()
  })
  .strict();

export const OwnerRecordSchema = z.object({}).loose();
export type OwnerRecord = z.infer<typeof OwnerRecordSchema>;

export const PortfolioSummarySchema = z
  .object({
    products: z.number().int().nonnegative().default(0),
    opportunities: z.number().int().nonnegative().default(0),
    activeWorkflows: z.number().int().nonnegative().default(0),
    pendingActions: z.number().int().nonnegative().default(0),
    openIncidents: z.number().int().nonnegative().default(0)
  })
  .loose();
export type PortfolioSummary = z.infer<typeof PortfolioSummarySchema>;

export const WorkflowSummarySchema = z
  .object({
    id: z.string().optional(),
    workflowName: z.string().optional(),
    workflow_name: z.string().optional(),
    workflowKey: z.string().optional(),
    kind: z.string().optional(),
    status: z.string().default("unknown"),
    currentStepIndex: z.number().int().nonnegative().optional(),
    totalSteps: z.number().int().positive().optional(),
    productId: z.string().nullable().optional(),
    product_id: z.string().nullable().optional(),
    updatedAt: z.string().optional(),
    updated_at: z.string().optional(),
    startedAt: z.string().nullable().optional(),
    started_at: z.string().nullable().optional(),
    blockedReason: z.string().optional()
  })
  .loose()
  .transform((value) => ({
    ...value,
    workflowName: value.workflowName ?? value.workflow_name ?? value.kind ?? "workflow",
    productId: value.productId ?? value.product_id ?? undefined,
    updatedAt: value.updatedAt ?? value.updated_at,
    startedAt: value.startedAt ?? value.started_at
  }));
export type WorkflowSummary = z.infer<typeof WorkflowSummarySchema>;

export const IncidentSummarySchema = z
  .object({
    id: z.string().optional(),
    severity: z.string().default("unknown"),
    status: z.string().default("unknown"),
    summary: z.string().default("Incident details unavailable"),
    category: z.string().optional(),
    detectedAt: z.string().optional(),
    detected_at: z.string().optional(),
    requiresHuman: z.boolean().optional(),
    requires_human: z.boolean().optional()
  })
  .loose()
  .transform((value) => ({
    ...value,
    detectedAt: value.detectedAt ?? value.detected_at,
    requiresHuman: value.requiresHuman ?? value.requires_human ?? false
  }));
export type IncidentSummary = z.infer<typeof IncidentSummarySchema>;

export const ActivationGateSummarySchema = z
  .object({
    ordinal: z.number().int().positive().optional(),
    gateOrdinal: z.number().int().positive().optional(),
    gate_ordinal: z.number().int().positive().optional(),
    name: z.string().optional(),
    gateName: z.string().optional(),
    gate_name: z.string().optional(),
    status: z.string().optional(),
    state: z.string().optional(),
    requiredChecks: z.array(z.string()).optional(),
    required_checks: z.array(z.string()).optional(),
    signedAt: z.string().optional(),
    signed_at: z.string().optional(),
    keyId: z.string().optional(),
    key_id: z.string().optional()
  })
  .loose()
  .transform((value) => ({
    ...value,
    ordinal: value.ordinal ?? value.gateOrdinal ?? value.gate_ordinal ?? 0,
    name: value.name ?? value.gateName ?? value.gate_name ?? "Unnamed gate",
    status: value.status ?? value.state ?? "unknown",
    requiredChecks: value.requiredChecks ?? value.required_checks ?? [],
    signedAt: value.signedAt ?? value.signed_at,
    keyId: value.keyId ?? value.key_id
  }));
export type ActivationGateSummary = z.infer<typeof ActivationGateSummarySchema>;

export type OwnerAction = "pause" | "resume" | "kill";
export type OwnerAuthenticationMode = "access" | "token";

export interface ControlResult {
  readonly accepted: boolean;
  readonly simulated: boolean;
  readonly action: OwnerAction;
  readonly message: string;
}
